import { debounce } from "lodash";
import React, { useCallback, useMemo, useRef } from "react";
import { Form } from "react-final-form";
import { useDispatch } from "react-redux";

import ExpenditureFormRow from "components/UI/_TODO/ExpenditureForm/ExpenditureFormRow";
import TemplateExpenditureSegments from "pages/HandBook/features/TemplateExpenditureSegments/TemplateExpenditureSegments";
import { templateExpendituresLocalEdit } from "pages/HandBook/model/thunks";
import { IHandBookTemplateExpenditure, ManualExpenditureType } from "pages/HandBook/model/types";
import useHandBooksTemplatesRow from "pages/HandBook/tabs/HandBooksTemplates/useHandBooksTemplatesRow";
import { filterExpenditureMaterials } from "pages/HandBook/utils/handbooksModifyWithEditings";
import { findSegmentsById } from "pages/HandBook/utils/segments";

import { useTemplateAddExpenditureMaterials } from "./useTemplateAddExpenditureMaterials";
import { useTypedSelector } from "app/store/typedUseSelector";
import { TableReusableCell } from "shared/ui/dataDisplay/TableReusable/TableReusableRow";
import Checkbox from "shared/ui/inputs/Checkbox/Checkbox";
import { INPUT_BASE_VARIANTS } from "shared/ui/inputs/InputBase";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { EXPENDITURE_TYPES } from "constants/constant";

import { checkHasChangedFields, getChangedFields } from "utils/formHelpers/getChangedFields";

import styles from "./TemplateAddExpenditure.module.scss";

const HANDBOOK_TEMPLATE_EXPENDITURE_REQUIRED_FIELDS = ["name", "measure"];

interface IProps {
  item: IHandBookTemplateExpenditure;
  /** ID подраздела шаблона */
  sectionId: number;
  hiddenCols: Record<string, boolean>;
}

const TemplateAddExpenditure: React.FC<IProps> = ({ item, sectionId, hiddenCols }) => {
  const dispatch = useDispatch();
  const { isChecked, onCheckItem } = useHandBooksTemplatesRow(item.id);
  const templateViewFilters = useTypedSelector((state) => state.handBookReducer.templateViewFilters);
  // Специально обрабатываем все материалы не обращая внимание на фильтры отображения
  const defaultMaterials = useMemo(() => filterExpenditureMaterials(item.materials), [item.materials]);
  const { submitMaterials, setMaterials, materials } = useTemplateAddExpenditureMaterials({
    sectionId,
    itemId: item.id,
    defaultMaterialsIds: defaultMaterials.map((m) => m.id),
  });
  const allSegments = useTypedSelector((state) => state.handBookReducer.allSegments);

  const initialValues = useRef(
    Object.assign(
      {},
      item.manual_expenditure || {},
      {
        expenditure_type: EXPENDITURE_TYPES.WORK,
        price: Number(item.price) || 0,
        count: Number(item.count) || 0,
        segment: item.segment,
        // @ts-ignore
        parent_segment: findSegmentsById(allSegments, item.segment).parentSegment?.id, // техническое поле
      },
      defaultMaterials.reduce((materialsFieldMap, material) => {
        materialsFieldMap[`id:${material.id}`] = {
          ...material.manual_expenditure,
          ...material,
          expenditure_type: material.manual_expenditure.type,
          _handbookExpenditure: material,
        };
        return materialsFieldMap;
      }, {} as Record<string, object>) || {}
    )
  );

  const submit = useCallback(
    (values: any, form: any) => {
      submitMaterials(values);
      const handbookChange = {
        name: values.name,
        measure: values.measure,
      };
      const templateChange = {
        count: values.count,
        price: values.price,
        parent_segment: values.parent_segment,
        segment: values.segment,
      };
      let handbookPayload;
      let templatePayload;
      if (checkHasChangedFields(handbookChange, initialValues.current)) {
        if (values._handbookExpenditure && !checkHasChangedFields(handbookChange, values._handbookExpenditure)) {
          handbookPayload = values._handbookExpenditure.id;
        } else {
          // Специально передаём весь объект, чтобы не терять поля по умолчанию
          handbookPayload = { ...handbookChange, type: "work" };
        }
      }
      if (checkHasChangedFields(templateChange, initialValues.current)) {
        templatePayload = Object.assign({}, getChangedFields(templateChange, initialValues.current), {
          parent_segment: values.parent_segment,
          section_id: sectionId,
          segment: values.segment,
        });
      }

      if (handbookPayload || templatePayload) {
        dispatch(
          templateExpendituresLocalEdit({
            expenditureId: item.id,
            expenditure: {
              handbook: handbookPayload,
              template: templatePayload,
            },
          })
        );
      }
    },
    [item.id]
  );

  const isDisplayParentSectionName = templateViewFilters?.attribute && !hiddenCols["Раздел"];
  const isDisplaySectionName = templateViewFilters?.attribute && !hiddenCols["Подраздел"];

  const rowPercents = [
    3,
    isDisplayParentSectionName ? 7 : undefined,
    isDisplaySectionName ? 9 : undefined,
    97 - (isDisplayParentSectionName ? 7 : 0) - (isDisplaySectionName ? 9 : 0),
  ].filter((p) => p !== undefined) as number[];

  return (
    <Form
      onSubmit={submit}
      initialValues={initialValues.current}
      render={({ values, handleSubmit, form }) => {
        const delayedSubmit = debounce(() => form.submit(), 50);
        const addMockResource = () => {
          const mockId = -Date.now();
          form.change(`id:${mockId}`, { expenditure_type: "material", type: "material" });
          setMaterials((prev) => [...prev, mockId]);
          form.submit();
        };

        return (
          <form
            onSubmit={handleSubmit}
            onReset={() => form.restart()}
            onMouseOut={delayedSubmit}
            className={styles.form}
            autoComplete="off"
          >
            <RowGrid percents={rowPercents}>
              <TableReusableCell isCentered>
                <Checkbox onCheck={onCheckItem} checked={isChecked} noMargin />
              </TableReusableCell>
              {isDisplayParentSectionName && (
                <TableReusableCell>
                  <span>{item.parent_section_name}</span>
                </TableReusableCell>
              )}
              {isDisplaySectionName && (
                <TableReusableCell>
                  <span>{item.section_name}</span>
                </TableReusableCell>
              )}
              <ExpenditureFormRow
                submit={() => form.submit()}
                requiredFields={HANDBOOK_TEMPLATE_EXPENDITURE_REQUIRED_FIELDS}
                inputVariant={INPUT_BASE_VARIANTS.SECONDARY}
                isWork
                showExpType={false}
                id={item.id}
                showLabels={false}
                gridClassName={styles.workGrid}
                onSelectExpenditure={(exp) => {
                  form.change("_handbookExpenditure", exp);
                  form.change("id", exp.id);
                  form.change("name", exp.name);
                  form.change("price", exp.price);
                  form.change("count", exp.count);
                  form.change("measure", exp.measure);
                  if (exp.expenditure_products) {
                    exp.expenditure_products.forEach((product) => {
                      form.change(`id:${product.id}`, {
                        ...product,
                        expenditure_type: product.type,
                        _handbookExpenditure: product,
                        _isNew: true,
                      });
                    });
                    setMaterials(exp.expenditure_products.map((p) => p.id));
                  }

                  form.submit();
                }}
                key={form.getState().values.id}
                expType="work"
                form={form}
                isWithNumberVariant={false}
                isWithActions={false}
                onAddNew={addMockResource}
                classNameAutocompleteContainer={styles.workAutocompleteContainerWithSegments}
                afterNameInner={
                  <TemplateExpenditureSegments
                    defaultSegment={item?.segment}
                    onChangeParentSegment={(segment: number | string | null) => {
                      form.change("parent_segment", segment);
                    }}
                    onChangeSegment={(segment: number | string | null) => {
                      form.change("segment", segment);
                      form.submit();
                    }}
                  />
                }
              />
            </RowGrid>
            {materials.map((el) => {
              const materialExpenditureType = form.getState().values[`id:${el}`]?.expenditure_type as
                | ManualExpenditureType
                | undefined;
              // Принимаем во внимание фильтры отображения
              if (materialExpenditureType && !templateViewFilters[materialExpenditureType]) {
                return null;
              }
              return (
                <div key={el} className={styles.newMaterialRow}>
                  <ExpenditureFormRow
                    submit={() => form.submit()}
                    requiredFields={HANDBOOK_TEMPLATE_EXPENDITURE_REQUIRED_FIELDS}
                    inputVariant={INPUT_BASE_VARIANTS.SECONDARY}
                    onCancel={() => {
                      setMaterials((prev) => prev.filter((m) => m !== el));
                      form.change(`id:${el}`, null);
                      form.submit();
                    }}
                    isWork={false}
                    id={el}
                    showLabels={false}
                    gridClassName={styles.materialGrid}
                    onSelectExpenditure={(exp) => {
                      // @ts-ignore
                      form.change(`id:${el}`, { expenditure_type: exp.type, ...exp, _handbookExpenditure: exp });
                      form.submit();
                    }}
                    key={materialExpenditureType}
                    expType={materialExpenditureType || "material"}
                    form={form}
                    isWithNumberVariant={false}
                    classNameActions={styles.deleteAction}
                  />
                </div>
              );
            })}
          </form>
        );
      }}
    />
  );
};

export default React.memo(TemplateAddExpenditure);
